import React, { useEffect, useState } from 'react';
import { listTenants } from './api';
import { useTranslation } from '../i18n';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch (e) { return '—'; }
};

// Map a subscription_status to a label + pill style.
const STATUS = {
  active: { label: 'Active', style: 'paid' },
  trialing: { label: 'Trialing', style: 'paid' },
  trial: { label: 'Trial', style: 'trial' },
  past_due: { label: 'Past due', style: 'due' },
  canceled: { label: 'Canceled', style: 'muted' },
  suspended: { label: 'Suspended', style: 'due' }
};

export default function Tenants() {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    listTenants()
      .then((res) => { if (!cancelled) setTenants(res.data || []); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || 'Could not load tenants'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const isActive = (s) => s === 'active' || s === 'trialing';
  const isTrial = (s) => s === 'trial';
  const isInactive = (s) => s === 'past_due' || s === 'canceled' || s === 'suspended';

  const counts = {
    total: tenants.length,
    active: tenants.filter((t) => isActive(t.subscription_status)).length,
    trial: tenants.filter((t) => isTrial(t.subscription_status)).length,
    inactive: tenants.filter((t) => isInactive(t.subscription_status)).length
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? tenants.filter(
        (t) =>
          String(t.name || '').toLowerCase().includes(q) ||
          String(t.email || '').toLowerCase().includes(q)
      )
    : tenants;

  if (loading) return <div style={styles.loading} role="status" aria-live="polite">Loading tenants…</div>;
  if (error) return <div style={styles.error} role="alert">{error}</div>;

  return (
    <div>
      <div style={styles.statsRow}>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Total companies</div>
          <div style={styles.statValue}>{counts.total}</div>
        </div>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Paying / trialing</div>
          <div style={{ ...styles.statValue, color: '#14653a' }}>{counts.active}</div>
        </div>
        <div style={styles.stat}>
          <div style={styles.statLabel}>On free trial</div>
          <div style={{ ...styles.statValue, color: '#b7791f' }}>{counts.trial}</div>
        </div>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Inactive</div>
          <div style={{ ...styles.statValue, color: '#b91c1c' }}>{counts.inactive}</div>
        </div>
      </div>

      <input
        id="tenants-search"
        style={styles.search}
        type="search"
        placeholder="Search by company or email…"
        aria-label={t('a11y.search_tenants')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {filtered.length === 0 ? (
        <div style={styles.empty}>No companies match.</div>
      ) : (
        <div style={styles.list}>
          {filtered.map((t) => {
            const st = STATUS[t.subscription_status] || { label: t.subscription_status || 'Unknown', style: 'muted' };
            const ref = Array.isArray(t.referrals) ? t.referrals[0] : null;
            const renews = t.subscription_status === 'trial' || t.subscription_status === 'trialing'
              ? t.trial_ends_at
              : t.current_period_end;
            const renewLabel = t.subscription_status === 'trial' || t.subscription_status === 'trialing'
              ? 'Trial ends'
              : 'Renews';
            return (
              <div key={t.id} style={styles.row}>
                <div style={styles.rowMain}>
                  <div style={styles.name}>{t.name || '(no name)'}</div>
                  <div style={styles.meta}>
                    {t.email || '—'}
                    {ref && ref.referral_partners?.name && (
                      <span style={styles.refTag}>via {ref.referral_partners.name}</span>
                    )}
                  </div>
                </div>
                <div style={styles.rowSide}>
                  <span style={{ ...styles.pill, ...pillStyle[st.style] }}>{st.label}</span>
                  <div style={styles.dates}>
                    {renews ? `${renewLabel} ${fmtDate(renews)}` : ''}
                    {t.created_at ? <span style={styles.joined}>Joined {fmtDate(t.created_at)}</span> : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const pillStyle = {
  paid: { background: '#ecf8ee', color: '#14653a' },
  trial: { background: '#fdf3e0', color: '#b7791f' },
  due: { background: '#fee2e2', color: '#b91c1c' },
  muted: { background: '#f0f2ec', color: '#5f6b58' }
};

const styles = {
  loading: { textAlign: 'center', padding: 40, color: '#5f6b58' },
  error: { background: '#FEF2F2', color: '#dc2626', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 12 },
  empty: { background: '#fff', border: '1px dashed #e3e9df', borderRadius: 12, padding: '24px 18px', fontSize: 13.5, color: '#5f6b58', textAlign: 'center' },

  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 },
  stat: { background: '#fff', border: '1px solid #e3e9df', borderRadius: 12, padding: 16 },
  statLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#5f6b58', marginBottom: 8 },
  statValue: { fontFamily: 'Manrope, sans-serif', fontSize: 24, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-.5px' },

  search: { width: '100%', padding: '11px 14px', border: '1px solid #e3e9df', borderRadius: 10, fontSize: 14, color: '#1a1a1a', background: '#fff', boxSizing: 'border-box', outline: 'none', marginBottom: 14, fontFamily: 'inherit' },

  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: '#fff', border: '1px solid #e3e9df', borderRadius: 10, padding: 14 },
  rowMain: { minWidth: 0, flex: 1 },
  name: { fontSize: 14.5, fontWeight: 700, color: '#1a1a1a' },
  meta: { fontSize: 12, color: '#5f6b58', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  refTag: { fontSize: 11, fontWeight: 700, color: '#14653a', background: '#ecf8ee', padding: '2px 8px', borderRadius: 100 },
  rowSide: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flexShrink: 0 },
  pill: { fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 100, textTransform: 'uppercase', letterSpacing: '0.02em' },
  dates: { fontSize: 11.5, color: '#5f6b58', textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 2 },
  joined: { color: '#5f6b58' }
};
